/**
 * Reuse the first working image URL for a given object path.
 * Spaces/S3 presigned GETs change query params on every API call; the browser
 * then treats each visit as a new download. Remembering the unexpired URL
 * lets the HTTP cache serve the same bytes.
 */

const rememberedByKey = new Map();

export function stableImageKey(src) {
  if (!src) return '';
  try {
    const u = new URL(src, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    return `${u.origin}${u.pathname}`;
  } catch {
    return String(src).split('?')[0];
  }
}

export function parseAmzDate(raw) {
  const m = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(String(raw || ''));
  if (!m) return null;
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), Number(m[6]));
}

export function isLikelyExpiredSignedUrl(url, now = Date.now()) {
  if (!url) return true;
  try {
    const u = new URL(url, 'http://localhost');
    const amzDate = u.searchParams.get('X-Amz-Date');
    const amzExpires = u.searchParams.get('X-Amz-Expires');
    if (amzDate && amzExpires) {
      const start = parseAmzDate(amzDate);
      if (start == null) return false;
      return now > start + Number(amzExpires) * 1000 - 15_000;
    }
    const expires = u.searchParams.get('Expires');
    if (expires) return now > Number(expires) * 1000 - 15_000;
  } catch {
    return false;
  }
  return false;
}

export function rememberLoadedImageSrc(src) {
  const key = stableImageKey(src);
  if (!key || !src) return;
  const existing = rememberedByKey.get(key);
  if (existing && !isLikelyExpiredSignedUrl(existing)) return;
  rememberedByKey.set(key, src);
}

export function getPreferredImageSrc(freshSrc) {
  if (!freshSrc) return '';
  const key = stableImageKey(freshSrc);
  const remembered = rememberedByKey.get(key);
  if (remembered && remembered !== freshSrc && !isLikelyExpiredSignedUrl(remembered)) {
    return remembered;
  }
  return freshSrc;
}

export function invalidateImageSrcCache() {
  rememberedByKey.clear();
}

export function peekLightboxItemUrl(item) {
  if (!item) return '';
  const raw = item.url || item.src || '';
  return getPreferredImageSrc(raw) || raw || '';
}
